/** Defaults tuned for fast Gemini identify (not archival photos). */
export const IDENTIFY_STILL_MAX_EDGE = 1024;
export const IDENTIFY_STILL_QUALITY = 0.72;

/** Sticker / rating-plate OCR needs more pixels than naming a mug. */
export const STICKER_STILL_MAX_EDGE = 1600;
export const STICKER_STILL_QUALITY = 0.88;

/** Grab a JPEG still from a live <video> element. */
export async function captureVideoJpeg(
  video: HTMLVideoElement,
  maxEdge = IDENTIFY_STILL_MAX_EDGE,
  quality = IDENTIFY_STILL_QUALITY,
): Promise<Blob | null> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
  });
}

/**
 * Freeze the preview the user is looking at, before any await.
 * ImageCapture.takePhoto() refocuses and waits ~1s, so the shot is whatever the phone sees after they move.
 */
function freezeVideoFrame(
  video: HTMLVideoElement,
  maxEdge: number,
): HTMLCanvasElement | null {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Still of the current preview. Pixels are copied on the tap, then encoded. */
export async function captureBestJpeg(
  video: HTMLVideoElement,
  _stream?: MediaStream | null,
  opts?: { maxEdge?: number; quality?: number },
): Promise<Blob | null> {
  const maxEdge = opts?.maxEdge ?? IDENTIFY_STILL_MAX_EDGE;
  const quality = opts?.quality ?? IDENTIFY_STILL_QUALITY;
  const canvas = freezeVideoFrame(video, maxEdge);
  if (!canvas) return null;
  return await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
  });
}

/** Compact base64 for edge-function identify (no storage round-trip). */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function roomsCaptureUrl(propertyId: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/properties/${propertyId}?tab=rooms`;
}

export function isDesktopCaptureViewport(): boolean {
  if (typeof window === "undefined") return false;
  const wide = window.innerWidth >= 1024;
  const finePointer = window.matchMedia("(pointer: fine)").matches;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  return wide && finePointer && !coarse;
}
